import { Component, EventEmitter, Output } from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { getCookie } from '../../../../../utils/getCookies';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { OrderKeyValueByDatePipe } from '../../../../../shared/pipes/order-keyvalue-by-date.pipe';
import { SocketService } from '../../../../../services/socket.service';
import { JsonCvComponent } from './modals/json-cv/json-cv.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ModalWithResponseComponent } from '../../../../user/modals/modal-with-response/modal-with-response.component';
import { ModalGeneralComponent } from '../../../../user/modals/modal-general/modal-general.component';


@Component({
  selector: 'app-CV-drive',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MatButtonModule, OrderKeyValueByDatePipe, MatDialogModule, TranslateModule],
  templateUrl: './CV-drive.component.html',
  styleUrl: './CV-drive.component.scss'
})
export class CVDriveComponent {

  // --- Outputs ---
  @Output() listCVsEvent = new EventEmitter<any[]>();
  @Output() openJobOfferLibraryEvent = new EventEmitter<void>();

  /**
   * Map of folders grouped by date (returned from backend).
   * Each key is a human readable date, containing its folders array.
   */
  folders: { [key: string]: any[] } = {};
  /**
   * Keeps the original folders payload to reapply filtering without
   * re-fetching from the API.
   */
  originalFolders: { [key: string]: any[] } = {};

  files: any[] = [];
  folderForm: FormGroup;
  newFolderForm: FormGroup;
  filesToUpload: File[] = [];
  folderId: string = '';
  selectedFiles: string[] = [];
  urlBase: string = environment.url;
  isDragOver = false;
  /**
   * Tracks current preprocessing index reported over socket
   * so the UI can surface progress feedback.
   */
  processingIndex = -1;
  addingFolder = false;
  totalFiles = 0;
  editingFolderId: string | null = null;
  editingFolderName = '';
  editingFolderSaving = false;
  id_analysis: string = '';

  constructor(private http: HttpClient, private formb: FormBuilder, private router: Router, private route: ActivatedRoute, private socketService: SocketService, private dialog: MatDialog) {
    this.folderForm = this.formb.group({
      file: ['', Validators.required],
    });
    this.newFolderForm = this.formb.group({
      name: ['', Validators.required],
    });



    // Listen for CV preprocessing progress
    this.socketService.onCVPreprocessing((data: any) => {
      this.processingIndex = data.index;
      this.totalFiles = data.total;
      if (data.index > 0 && data?.type === "partial"){
        this.getFiles(this.folderId);
      }
    });

    // Listen for CV preprocessing completion to reset progress indicator
    this.socketService.onCVPreprocessingCompleted((data: any) => {
      this.processingIndex = -1;
      this.allfilesuploaded = []
    });

    // get the id_analysis from the query params
    this.route.queryParams.subscribe((params: any) => {
      if (params["id_analysis"] || localStorage.getItem('conectio_client_id_analysis')) {
        this.id_analysis = params["id_analysis"] || localStorage.getItem('conectio_client_id_analysis') || '';
        this.getSelectedFilesFromAnalysis();
            // bootstrap initial folders list
        this.getFolders();
      }
      else {
        this.router.navigate(['/dashboard/company/cv-deep-analyzer']);
      }
    });

  }


  // we get the selected files from the analysis selected
  biggest_folder_id: string = '';
  is_new_analysis: boolean = false;
  saved_selected_files: any[] = [];
  getSelectedFilesFromAnalysis() {
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.get(`${environment.url}/api/v1/CVDeepAnalyzer/analyzer/get_analysis_data`, { headers, params: { id_analysis: this.id_analysis } }).subscribe((res: any) => {
      this.selectedFiles = res.CVs;
      this.saved_selected_files = [...res.CVs];
      this.biggest_folder_id = res.biggest_folder_id;
      this.is_new_analysis = res.totalJobOffers === 0;
    });
  }



  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.files && input.files.length > 0) {
      this.queueFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onFileDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onFileDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.queueFiles(Array.from(files));
    }
  }

  /**
   * Adds the incoming files to the local queue and triggers upload for the active folder.
   */
  private queueFiles(files: File[]) {
    files.forEach((file) => {
      this.filesToUpload.push(file);
    });

    this.addFile(this.folderId);
  }


  loadingFiles = false;
  /**
   * Fetch files inside a folder and refresh grid after any mutation.
   */
  getFiles(folderId: string) {
    this.loadingFiles = true;
    this.files = [];
    this.folderId = folderId;
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.get(`${environment.url}/api/v1/CVDeepAnalyzer/CV/get_files`, { headers, params: { id_folder: folderId } }).subscribe((res: any) => {
      this.files = res.files;
      this.loadingFiles = false;
    }, (error: any) => {
      this.loadingFiles = false;
      console.error('Error getting files', error);
    });
  }

  onFolderSelected(folder: any) {
    if (this.editingFolderId === folder.id) {
      return;
    }
    this.getFiles(folder.id);
  }

  /**
   * Fetch folders so the left panel can be rendered.
   */
  loadingFolders = false;
  max_folder_id: string = '';
  getFolders() {
    this.loadingFolders = true;
    this.folders = {};
    this.originalFolders = {};
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.get(`${environment.url}/api/v1/CVDeepAnalyzer/CV/get_folders`, { headers, params: { id_analysis: this.id_analysis } }).subscribe((res: any) => {
      this.folders = res.folders;
      this.originalFolders = res.folders;
      this.max_folder_id = res.max_folder_id;
      this.loadingFolders = false;

      const allFolders = Object.values(this.folders).flat();

    // After loading folders, autoselect the first folder if any exists and none currently selected.
    if (!this.folderId) {
      if (allFolders.length > 0) {

        const folder_selected = allFolders.find((folder: any) => folder.id === this.max_folder_id);
        if (folder_selected) {
          this.onFolderSelected(folder_selected);
        }else{
          this.onFolderSelected(allFolders[0]);
        }
      }
    }else{
      const folder_selected = allFolders.find((folder: any) => folder.id === this.folderId);
      if (folder_selected) {
        this.onFolderSelected(folder_selected);
      }else{
        this.onFolderSelected(allFolders[0]);
      }

    }
    }, (error: any) => {
      this.loadingFolders = false;
      console.error('Error getting folders', error);
    });
  }

  /**
   * Filters `folders` in-memory to avoid round trips while searching.
   */
  filterFolders(event: Event) {
    const search = (event.target as HTMLInputElement).value;

    if (search.length > 0) {
      // Create a new object to avoid mutating originalFolders
      const filteredFolders: any = {};
      Object.keys(this.originalFolders).forEach((key) => {
        const filtered = this.originalFolders[key].filter((folder: any) =>
          folder.name.toLowerCase().includes(search.toLowerCase())
        );
        filteredFolders[key] = filtered;
      });
      this.folders = filteredFolders;
    } else {
      this.folders = this.originalFolders;
    }
  }


  loading = false;
  /**
   * Upload queued files to the active folder and notify backend to preprocess them.
   */
  allfilesuploaded:any[] = [];
  addFile(folderId: string) {
    if (!folderId || this.filesToUpload.length === 0) {
      return;
    }

    this.loading = true;
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`,
    });

    const formData: FormData = new FormData();
    this.filesToUpload.forEach((file) => {
      formData.append('files[]', file, file.name);
    });
    formData.append('id_folder', folderId);
    this.allfilesuploaded = this.filesToUpload.map((file) => file.name);
    this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/CV/save_file`, formData, { headers }).subscribe({
      next: (res: any) => {
        this.filesToUpload = [];
        this.loading = false;
        
        // once we know the files, we emit to preprocess the files
        this.socketService.sendCVToPreprocess({ CVFilesIds: res.CVFilesIds });

      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error uploading files', error);

        // we check if is for a type error
        if (error.error.error === 'files_wrong_extensions') {
          // we open the modal with the error
          ModalWithResponseComponent.open(this.dialog, {
            title: 'CVDeepAnalyzer.CVDrive.filesWrongExtensionsTitle',
            message: 'CVDeepAnalyzer.CVDrive.filesWrongExtensionsMessage',
            buttonText: 'CVDeepAnalyzer.CVDrive.filesWrongExtensionsButton',
            buttonTextCancel: 'CVDeepAnalyzer.CVDrive.filesWrongExtensionsButtonCancel',
            type: 'error'
          });
        }
        else {
          // we open the modal with the error
          ModalWithResponseComponent.open(this.dialog, {
            title: 'CVDeepAnalyzer.errors.' + error.error.error + '.title',
            message: 'CVDeepAnalyzer.errors.' + error.error.error + '.message',
            buttonText: 'CVDeepAnalyzer.errors.' + error.error.error + '.buttonText',
            buttonTextCancel: 'CVDeepAnalyzer.errors.' + error.error.error + '.buttonTextCancel',
            type: 'error'
          });
        }
      }
    });
  }

  /**
   * Create a new folder by posting only its name; afterwards refresh the directory tree.
   */
  addFolder() {
    this.loading = true;
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/CV/create_folder`, { name: this.newFolderForm.value.name }, { headers }).subscribe((res: any) => {
      this.getFolders();
      this.newFolderForm.reset();
      this.loading = false;
    }, (error: any) => {
      this.loading = false;
      console.error('Error adding folder', error);
    });
  }

  /**
   * Delete a file and refresh the file list.
   */
  deleteFile(fileId: string, event: Event) {
    event.stopPropagation();
    // we open the confirm modal
    ModalWithResponseComponent.open(this.dialog, {
      title: 'CVDeepAnalyzer.CVDrive.deleteFileTitle',
      message: 'CVDeepAnalyzer.CVDrive.deleteFileMessage',
      buttonText: 'CVDeepAnalyzer.CVDrive.deleteFileButton',
      buttonTextCancel: 'CVDeepAnalyzer.CVDrive.deleteFileButtonCancel',
    }).afterClosed().subscribe((result: any) => {
      if (result) {
        const accessToken = getCookie('access_token');
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`
        });
        this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/CV/delete_file`, { id_file: fileId }, { headers }).subscribe((res: any) => {
          this.getFiles(this.folderId);
        }, (error: any) => {
          console.error('Error deleting file', error);
        });
      }
    });
  }

  // delete folder
  deleteFolder(folderId: string, event: Event) {
    event.stopPropagation();

    // we open the confirm modal
    ModalWithResponseComponent.open(this.dialog, {
      title: 'CVDeepAnalyzer.CVDrive.deleteFolderTitle',
      message: 'CVDeepAnalyzer.CVDrive.deleteFolderMessage',
      buttonText: 'CVDeepAnalyzer.CVDrive.deleteFolderButton',
      buttonTextCancel: 'CVDeepAnalyzer.CVDrive.deleteFolderButtonCancel',
    }).afterClosed().subscribe((result: any) => {
      if (result) {
        const accessToken = getCookie('access_token');
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`
        });
        this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/CV/delete_folder`, { id_folder: folderId }, { headers }).subscribe((res: any) => {
          this.getFolders();
        }, (error: any) => {
          console.error('Error deleting folder', error);
        });
      }
    });
  }

  startEditingFolder(folder: any, event: Event) {
    event.stopPropagation();
    this.editingFolderId = folder.id;
    this.editingFolderName = folder.name;
  }

  cancelEditingFolder(event?: Event) {
    event?.stopPropagation();
    this.editingFolderId = null;
    this.editingFolderName = '';
    this.editingFolderSaving = false;
  }

  saveFolderName(folder: any, event: Event) {
    event.stopPropagation();
    if (!this.editingFolderId || this.editingFolderId !== folder.id || this.editingFolderSaving) {
      return;
    }

    const trimmedName = this.editingFolderName.trim();
    if (!trimmedName) {
      return;
    }

    this.editingFolderSaving = true;
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });

    this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/CV/edit_folder_name`, { id_folder: folder.id, name: trimmedName }, { headers }).subscribe({
      next: () => {
        folder.name = trimmedName;
        this.cancelEditingFolder();
      },
      error: (error: any) => {
        console.error('Error updating folder name', error);
        this.editingFolderSaving = false;
      }
    });
  }


  /**
   * Toggle selection state for a file card. Multiple selection is allowed.
   */
  saveSelectedFile(fileId: string) {

    if (this.saved_selected_files.includes(fileId)) {
      return;
    }

    if (this.selectedFiles.includes(fileId) ) {
      this.selectedFiles = this.selectedFiles.filter((id) => id !== fileId);
    } else {
      this.selectedFiles.push(fileId);
    }
  }

  /**
   * Emits the array of selected file IDs to the parent component.
   */
  savingSelectedCVs = false;
  setselectedCVs() {
    this.savingSelectedCVs = true;
    const accessToken = getCookie('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });
    this.http.post(`${environment.url}/api/v1/CVDeepAnalyzer/analyzer/update_analysis`, { id_analysis: this.id_analysis, id_CVs: this.selectedFiles, only_cvs: 1}, { headers }).subscribe((res: any) => {
      this.savingSelectedCVs = false;

      // if is a new analysis, we open the job offer library modal, if not, we navigate to the analyzer page to update the analysis
      if (this.is_new_analysis) {
        this.openJobOfferLibraryEvent.emit();
      }
      else {
        this.router.navigate(['/dashboard/company/cv-deep-analyzer/analyzer'], { queryParams: { id_analysis: this.id_analysis } });
      }

    }, (error: any) => {
      this.savingSelectedCVs = false;
      console.error('Error setting selected CVs', error);
    });
  }


  openJsonCv(jsonCV: any, event: Event) {
    event.stopPropagation();
    this.dialog.open(JsonCvComponent, {
      data: { json: jsonCV },
      width: '60vw',
      height: '80vh',
    });
  }

  setAllSelectedCVs() {

    if (this.selectedFiles.length === this.files.length && this.selectedFiles.length > 0) {
      this.selectedFiles = [];
    } else {
      this.selectedFiles = this.files.map((file) => file.id);
    }

  }

  calculateMinutesToAnalyze() {
    // length of the files to upload * 20 seconds / 60 seconds
    const calculate = this.filesToUpload.length * 20 / 60;
    return Math.round(calculate) === 0 ? 1 : Math.round(calculate);
  }
}
